import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSignup } from "./api";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { signupSchema, SignupFormData } from "./schemas/signupSchema";
import pwVisbleIcon from "../../assets/pw_visible.svg";
import { toast } from "react-toastify";
import zxcvbn from "zxcvbn";
import spiderImage from "../../assets/spider_image.png";

export default function SignUp() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const signupMutation = useSignup();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = (data: SignupFormData) => {
    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }
    const result = zxcvbn(data.password, [data.username, data.email]);
    if (result.score < 3) {
      toast.error(
        result.feedback.warning || "Password is too weak or too common"
      );
      return;
    }
    setServerError("");
    // console.log("Signup form values:", data.password, data.confirmPassword);

    signupMutation.mutate(data, {
      onSuccess: () => {
        toast.success("An activation link has been sent to your email");
        navigate("/log-in");
      },
      onError: (err: any) => {
        const msg =
          err.response?.data?.message ||
          (err.response?.data?.errors?.[0]?.msg ??
            "An server error occurred during signup. Please try again later.");
        setServerError(msg);
      },
    });
  };

  useEffect(() => {
    if (serverError) toast.error(serverError);
  }, [serverError]);

  return (
    <div className='flex flex-col items-center justify-center px-4 md:px-0'>
      <div className='flex border-solid border-2 border-[#D9D9D9] w-full rounded-md flex items-center justify-center px-8 py-6 space-y-4 sm:w-full md:w-1/2 lg:w-1/3'>
        <div className="w-full mr-4">
          <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
            <div className='flex flex-col w-full pb-3'>
              <label htmlFor='signup-username' className='mb-1'>
                Username
              </label>
              <input
                id='signup-username'
                type='text'
                autoComplete='username'
                {...register("username")}
                className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
                placeholder='Username'
              />
              {errors.username && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.username.message}
                </p>
              )}
            </div>

            <div className='flex flex-col w-full mt-4 pb-3'>
              <label htmlFor='signup-email' className='mb-1'>
                Email
              </label>
              <input
                id='signup-email'
                type='email'
                autoComplete='email'
                {...register("email")}
                className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
                placeholder='Email'
              />
              {errors.email && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className='flex flex-col w-full mt-4 relative pb-3'>
              <label htmlFor='signup-password' className='mb-1'>
                Password
              </label>
              <input
                id='signup-password'
                type={isPasswordVisible ? "text" : "password"}
                autoComplete='new-password'
                {...register("password")}
                className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
                placeholder='Password'
              />
              <img
                src={pwVisbleIcon}
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                alt={isPasswordVisible ? "Hide password" : "Show password"}
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                className='w-8 h-8 absolute right-2 top-8 cursor-pointer'
              />
              {errors.password && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className='flex flex-col w-full mt-4 relative pb-3'>
              <label htmlFor='signup-confirm-password' className='mb-1'>
                Confirm Password
              </label>
              <input
                id='signup-confirm-password'
                type={isConfirmPasswordVisible ? "text" : "password"}
                autoComplete='new-password'
                {...register("confirmPassword")}
                className='border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-1'
                placeholder='Confirm Password'
              />
              <img
                src={pwVisbleIcon}
                onClick={() =>
                  setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
                }
                alt={isConfirmPasswordVisible ? "Hide password" : "Show password"}
                aria-label={
                  isConfirmPasswordVisible ? "Hide password" : "Show password"
                }
                className='w-8 h-8 absolute right-2 top-8 cursor-pointer'
              />
              {errors.confirmPassword && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type='submit'
              disabled={signupMutation.isPending}
              className='bg-[#0275D8] text-white rounded-md w-full py-2 mt-8 hover:bg-[#0256A1] disabled:bg-gray-400'
            >
              {signupMutation.isPending ? "Signing Up..." : "Sign Up"}
            </button>
          </form>

          <div className='flex flex-row items-start justify-start w-full pt-4 pb-2'>
            <Link to='/log-in'>
              <p className='underline underline-offset-2 cursor-pointer'>
                Already have an account?
              </p>
            </Link>
          </div>
        </div>
        <img src={spiderImage} alt='Spider' className='w-16 sm:w-16 md:w-20 lg:w-24 pt-16' />
      </div>
      <div className="px-8 mt-4 pb-8">
        <p className="text-sm text-gray-700">We crawl the web everyday capturing information on Cybercrime and use Open AI Technology (like ChatGPT) to query and retrieve the information</p>
      </div>
    </div>
  );
}
